import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Divider,
  Grid,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NotificationManager } from "react-notifications";
import { UpdateLabOrder } from "../../services/OrderService";
import { MenuItem, Select, InputLabel, FormControl } from "@mui/material";
import { orderStatusList } from "../common/statusLists";
import OrderTypeSelect from "../common/OrderTypeSelect";

// Same look as Edit Invoice's read-only fields (VLP's grey boxes).
const readOnlyInputProps = { readOnly: true, sx: { backgroundColor: "action.hover" } };

const EditOrderInfo = ({ order, onUpdate }) => {
  const [formData, setFormData] = useState(order);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFormData(order);
  }, [order]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle clientType change from OrderTypeSelect
  const handleClientTypeChange = (newClientType) => {
    setFormData((prevData) => ({
      ...prevData,
      clientType: newClientType,
    }));
  };
  // Server returns naive datetime strings (no timezone suffix). Passing them through
  // new Date() + toISOString() silently shifts them by the browser's UTC offset.
  // Slice the string directly so no conversion ever happens.
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const datePart = dateString.split("T")[0] ?? "";
    if (!datePart || datePart.startsWith("2000-01-01")) return "";
    return datePart; // already YYYY-MM-DD
  };

  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return "";
    if (dateString.startsWith("2000-01-01")) return "";
    return dateString.slice(0, 16); // YYYY-MM-DDTHH:mm, no UTC shift
  };
  const handleSave = async () => {
    setLoading(true);
    try {
      await UpdateLabOrder(formData);
      onUpdate(formData);
      NotificationManager.success("Order updated successfully!", "Success");
    } catch (err) {
      NotificationManager.error("Error updating order. " + err, "Error");
    } finally {
      setLoading(false);
    }
  };

  // readOnly marks fields VLP's OrderEntry shows read-only (grey box or display-only label).
  const renderTextField = (name, label, type = "text", readOnly = false) => (
    <Grid item xs={12} sm={6} md={3}>
      <TextField
        fullWidth
        label={label}
        name={name}
        value={formData[name] || ""}
        onChange={handleChange}
        variant="outlined"
        size="small"
        type={type}
        InputProps={readOnly ? readOnlyInputProps : undefined}
      />
    </Grid>
  );

  const renderCheckbox = (name, label, disabled = false) => (
    <Grid item xs={12} sm={6} md={3}>
      <FormControlLabel
        control={
          <Checkbox
            name={name}
            checked={formData[name] || false}
            onChange={handleChange}
            size="small"
            disabled={disabled}
          />
        }
        label={label}
      />
    </Grid>
  );

  if (!formData) {
    return <div>No order data available.</div>;
  }

  return (
    <Paper elevation={3} sx={{ padding: 3, marginTop: 1 }}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Order Overview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* 15-column grid from md up so md={3} items land 5 per row */}
          <Grid container spacing={2} columns={{ xs: 12, md: 15 }}>
            {renderTextField("subOrder", "Sub Order")}
            {renderTextField("projectNumber", "Project Number")}
            {renderTextField("clientName", "Client Name")}
            {renderTextField("accountNumber", "Account Number")}
            {renderTextField("orderPayType", "Order Pay Type", "text", true)}
            {renderTextField("clientCode", "Client Code")}
            {renderTextField("poNumber", "PO Number")}
            {renderTextField("projectCategory", "Project Description")}
            {renderTextField("specialInstructions", "Special Instructions", "text", true)}
            {renderTextField("billingCode", "Billing Code")}

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  id="status"
                  name="status"
                  value={formData.status?.trim() ?? ""}
                  label="Status"
                  onChange={handleChange}
                >
                  {orderStatusList.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <OrderTypeSelect
                selectedType={formData.clientType}
                onTypeChange={handleClientTypeChange}
              />
            </Grid>

            {renderTextField("companyName", "Company Name")}
            {renderTextField("priority", "Priority")}
            {renderCheckbox("hold", "On Hold")}
            {renderCheckbox("willRushPay", "Will Rush Pay", true)}

            <Grid item xs={12} md={15}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Internal Comments"
                name="internalComments"
                value={formData.internalComments || ""}
                onChange={handleChange}
                variant="outlined"
                size="small"
                helperText="Visible to lab staff only"
              />
            </Grid>

            <Grid item xs={12} md={15}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Report Notes"
                name="reportNotes"
                value={formData.reportNotes || ""}
                onChange={handleChange}
                variant="outlined"
                size="small"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Order Related Dates</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* 18-column grid from md up so md={3} items land 6 per row */}
          <Grid container spacing={2} columns={{ xs: 12, md: 18 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Date Created"
                name="dateCreated"
                value={formatDateForInput(formData.dateCreated)}
                InputProps={readOnlyInputProps}
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Date Received"
                name="dateReceived"
                value={formatDateForInput(formData.dateReceived)}
                onChange={handleChange}
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Date Completed"
                name="dateCompleted"
                value={formatDateForInput(formData.dateCompleted)}
                onChange={handleChange}
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Report Due Date"
                name="reportDueDate"
                value={formatDateForInput(formData.reportDueDate)}
                onChange={handleChange}
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Invoice Due Date"
                name="invoiceDueDate"
                value={formatDateForInput(formData.invoiceDueDate)}
                InputProps={readOnlyInputProps}
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Date Reported"
                name="dateReported"
                value={formatDateForInput(formData.dateReported)}
                onChange={handleChange}
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Reviewed Time"
                name="reviewedTime"
                value={formatDateTimeForInput(formData.reviewedTime)}
                InputProps={readOnlyInputProps}
                variant="outlined"
                size="small"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Billing Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* 15-column grid from md up so md={3} items land 5 per row */}
          <Grid container spacing={2} columns={{ xs: 12, md: 15 }}>
            {renderTextField("billingName", "Billing Name")}
            {renderTextField("billingContact", "Billing Contact")}
            {renderTextField("billingAddress", "Billing Address")}
            {renderTextField("billingAddress2", "Billing Address 2")}
            {renderTextField("billingCity", "Billing City")}
            {renderTextField("billingState", "Billing State")}
            {renderTextField("billingZip", "Billing Zip")}
            {renderTextField("billingPhone", "Billing Phone")}
            {renderTextField("billingFax", "Billing Fax")}
            {renderTextField("billingEmail", "Billing Email", "email")}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Reporting Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* 15-column grid from md up so md={3} items land 5 per row */}
          <Grid container spacing={2} columns={{ xs: 12, md: 15 }}>
            {renderTextField("reportingName", "Reporting Name")}
            {renderTextField("reportContact", "Report Contact")}
            {renderTextField("reportAddress", "Report Address")}
            {renderTextField("reportAddress2", "Report Address 2")}
            {renderTextField("reportCity", "Report City")}
            {renderTextField("reportState", "Report State")}
            {renderTextField("reportZip", "Report Zip")}
            {renderTextField("reportPhone", "Report Phone")}
            {renderTextField("reportFax", "Report Fax")}
            {renderTextField("reportEmail", "Report Email", "email")}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </Box>
    </Paper>
  );
};

export default EditOrderInfo;